import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Custom hook that mimics React 19's useOptimistic for optimistic updates.
 *
 * This hook allows you to maintain an optimistic state that updates immediately
 * while async operations (like debounced API calls) are pending. It prevents
 * stale updates from overwriting newer user input.
 *
 * @param baseValue - The base/authoritative value from the store/API
 * @param areEqual - Optional function to compare values for equality
 * @returns A tuple of [optimisticValue, setOptimisticValue]
 *
 * @example
 * ```tsx
 * const [value, setValue] = useOptimistic(storeValue);
 *
 * <input
 *   value={value}
 *   onChange={(e) => {
 *     setValue(e.target.value); // Updates immediately
 *     debouncedUpdate(e.target.value); // Updates store later
 *   }}
 * />
 * ```
 */

export function useOptimistic<T>(
  baseValue: T,
  areEqual: (a: T, b: T) => boolean = (a, b) => a === b
): [T, (value: T) => void] {
  const [optimisticValue, setOptimisticValue] = useState<T>(baseValue);
  const lastSetValueRef = useRef<T>(baseValue);
  const isPendingRef = useRef(false);

  // Update optimistic value when base value changes, but only if:
  // 1. The base value matches what we last set (legitimate update)
  // 2. OR there's no pending optimistic update
  useEffect(() => {
    if (areEqual(baseValue, lastSetValueRef.current)) {
      // This is a legitimate update from our optimistic change
      setOptimisticValue(baseValue);
      isPendingRef.current = false;
    } else if (!isPendingRef.current) {
      // No pending update, so sync with base (e.g., draft loaded, external update)
      setOptimisticValue(baseValue);
      lastSetValueRef.current = baseValue;
    }
    // Otherwise, ignore stale updates to prevent backtracking
  }, [baseValue, areEqual]);

  const setOptimistic = useCallback((value: T) => {
    setOptimisticValue(value);
    lastSetValueRef.current = value;
    isPendingRef.current = true;
  }, []);

  return [optimisticValue, setOptimistic];
}
